import { Observable, combineLatest } from 'rxjs';
import { filter, map, startWith, tap, withLatestFrom } from 'rxjs/operators';
import {
  IsometricBlockDiagramViewProperties,
  IsometricBlockDiagramViewSettings,
} from './types';
import { fieldInterface } from '@ardoq/field-interface';
import {
  action$,
  combineReducers,
  dispatchAction,
  ofType,
  reducer,
  streamReducer,
  withNamespace,
} from '@ardoq/rxbeach';
import { isTruthy, returnTrue, setStateProperty } from '@ardoq/common-helpers';
import {
  APIFieldType,
  APIOrganizationUser,
  DiffMode,
  ViewIds,
} from '@ardoq/api-types';
import type { I18nLocale } from '@ardoq/i18n';
import { WorkspaceModeInstanceViewSettings } from 'views/environmentals/viewSettings/types';
import { getFilteredViewSettings$ } from 'views/filteredViewSettings$';
import {
  getComponentIdsFromViewModel,
  getDefaultRelationshipDiagramStateForWorkspaceMode,
} from 'tabview/relationshipDiagrams/utils';
import { viewSettingsConsts } from '@ardoq/view-settings';
import {
  allActiveOrgUsers$,
  globalViewModelResetSource$,
  SharedBlockDiagramsRenderingDataSource,
  sharedBlockDiagramsRenderingDataSource$,
} from 'tabview/relationshipDiagrams/shared/sharedGlobalStreams';
import { notifyViewLoading } from 'tabview/actions';
import { getGraphComponentLegendProps } from 'tabview/graphComponent/graphComponentLegendUtils';
import { filterInterface } from '@ardoq/filter-interface';
import {
  collapseAllGroupsReducer,
  expandAllGroupsReducer,
  toggleCollapseGroupReducer,
} from 'tabview/relationshipDiagrams/viewModel$/toggleCollapseGroupInWorkspaceMode';
import { readViewComponentIds } from 'streams/viewComponentIds$';
import {
  components$,
  componentsChanged$,
  ComponentsState,
} from 'streams/components/components$';
import { cloneGraphItemsAsUpdate, isSeparateReferencesChange } from '../utils';
import {
  GlobalRelationshipsViewModelResetStreamShape,
  ViewDataLimitCheck,
} from 'tabview/relationshipDiagrams/viewModel$/types';
import type { ViewDataLimitExceeded } from 'views/environmentals/viewDataLimits';
import {
  DATA_LIMIT_CHECK_DISABLED,
  buildViewDataLimitCheck,
} from 'tabview/relationshipDiagrams/consts';
import {
  legacyGetDistinctReferenceKey,
  getGraphNodeCount,
  RelationshipDiagramViewModelStreamState,
} from '@ardoq/graph';
import { handleComponentsUpdate } from 'tabview/relationshipDiagrams/viewModel$/handleComponentUpdate';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import {
  DEFAULT_EXPECTED_DURATION,
  startViewLoadTracking,
} from 'tracking/durationTracking/viewPerformance';
import { buildViewModel } from 'tabview/relationshipDiagrams/viewModel$/buildViewModel';
import {
  relationshipDiagramBypassDataLimit,
  relationshipDiagramCollapseAllGroups,
  relationshipDiagramExpandAllGroups,
  relationshipDiagramToggleCollapseGroup,
} from 'tabview/relationshipDiagrams/actions';
import { getDefaultRelationshipDiagramsViewInterface } from 'tabview/relationshipDiagrams/viewModel$/getDefaultRelationshipDiagramsViewInterface';
import { scopeDiff$ } from 'scope/scopeDiff$';
import { activeDiffMode$ } from 'scope/activeDiffMode$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { quickPerspectiveReferenceTypeFilters$ } from 'streams/filters/quickPerspectiveReferenceTypeFilters$';
import { getErrorInfoBoxLabels } from 'locales/getErrorInfoBoxLabels';

const VIEW_ID = ViewIds.ISOMETRIC_BLOCK_DIAGRAM;

const emptySourceDataState: Omit<SourceDataShape, 'viewInterface'> = {
  ...getDefaultRelationshipDiagramStateForWorkspaceMode(VIEW_ID),
  dataLimitCheck: DATA_LIMIT_CHECK_DISABLED,
  dataLimitExceeded: null,
};

const IGNORE_VIEW_SETTINGS: (keyof IsometricBlockDiagramViewSettings)[] = [
  'separateReferences',
  viewSettingsConsts.IS_LEGEND_ACTIVE,
  'collapsedGroupIds',
];

const cloneViewModelForAnimation = (
  state: SourceDataShape,
  newSettingsWithChanges: WorkspaceModeInstanceViewSettings<ViewIds.ISOMETRIC_BLOCK_DIAGRAM>
): SourceDataShape => ({
  ...state,
  viewModel: {
    ...state.viewModel,
    ...cloneGraphItemsAsUpdate(state.viewModel),
  },
  viewSettings: {
    ...state.viewSettings,
    separateReferences:
      newSettingsWithChanges.currentSettings.separateReferences,
  },
});

type ViewModelResetStreamShape =
  GlobalRelationshipsViewModelResetStreamShape & {
    viewSettings: IsometricBlockDiagramViewSettings;
    dataLimitCheck: ViewDataLimitCheck;
  };

type SourceDataShape =
  RelationshipDiagramViewModelStreamState<IsometricBlockDiagramViewSettings> & {
    viewId: ViewIds.ISOMETRIC_BLOCK_DIAGRAM;
    dataLimitCheck: ViewDataLimitCheck;
    dataLimitExceeded: ViewDataLimitExceeded | null;
  };

const sourceDataResetReducer = (
  previousState: SourceDataShape,
  {
    context,
    components,
    scenarioRelatedComponentIds,
    graphModel,
    scenario,
    loadedGraph,
    viewSettings,
    i18nLocale,
    dataLimitCheck,
  }: ViewModelResetStreamShape
): SourceDataShape => {
  const { viewInstanceId, viewInterface, scopeDiffState } = previousState;

  const [loadedScenarioData, scenarioRelatedComponents, { showScopeRelated }] =
    scenario;

  const performanceTrackingId = startViewLoadTracking(
    viewIdToUserFriendlyName[VIEW_ID],
    DEFAULT_EXPECTED_DURATION
  );

  const { rawGraph, nodeLabelsData, dataLimitExceeded, ...viewModel } =
    buildViewModel({
      viewInterface,
      context,
      components,
      scenarioRelatedComponentIds,
      viewSettings,
      graphModel,
      loadedScenarioData,
      scenarioRelatedComponents,
      isInScopeDiffMode: Boolean(scopeDiffState.scopeDiff),
      scopeDiff: scopeDiffState.scopeDiff,
      expandedStateChangingGroupId: null,
      showScopeRelated,
      getDistinctReferenceKey: legacyGetDistinctReferenceKey,
      shouldCollapseGraph: true,
      viewId: VIEW_ID,
      i18nLocale,
      dataLimitCheck,
    });

  return {
    ...previousState,
    dataLimitCheck,
    dataLimitExceeded,
    viewModel: {
      ...viewModel,
      nodeLabelsData,
    },
    viewSettings,
    streamState: {
      context,
      components,
      scenarioRelatedComponentIds,
      graphModel,
      loadedScenarioData,
      scenarioRelatedComponents,
      showScopeRelated,
      skipRender: false, // To be removed, we filter the view settgins on the source stream level
      isUpdatingAppState: false, // To be removed, we don't emit source data updates when the app state updates
      rawGraph,
      getDistinctReferenceKey: legacyGetDistinctReferenceKey,
      performanceTrackingId,
    },
    viewInstanceId,
    noContext: false,
    loadedGraph,
    viewId: VIEW_ID,
  };
};

type MapToViewPropsArgs = [
  SourceDataShape,
  SharedBlockDiagramsRenderingDataSource,
  ComponentsState,
];
type IsometricBlockDiagramViewPropertiesBeforeLegend = Omit<
  IsometricBlockDiagramViewProperties,
  | 'legendProps'
  | 'viewSettings'
  | 'quickPerspectiveReferenceTypeFilters'
  | 'i18nLocale'
  | 'errorInfoBoxLabels'
  | 'streamState'
> & {
  streamState: Pick<
    SourceDataShape['streamState'],
    'rawGraph' | 'loadedScenarioData'
  >;
};

const modelToProps = ([
  viewModel,
  { clearEdges, isContextChange, scenarioName },
  { byId: componentsById },
]: MapToViewPropsArgs): IsometricBlockDiagramViewPropertiesBeforeLegend => {
  const viewSettings = viewModel.viewSettings;
  const allComponentNodes = [
    ...viewModel.viewModel.nodes.add,
    ...viewModel.viewModel.nodes.update,
  ]
    .map(nodeId => viewModel.viewModel.nodes.byId.get(nodeId))
    .filter(isTruthy);
  const fieldValuesByModelId = new Map<string, number | null>(
    allComponentNodes.map((graphNode): [string, number | null] => {
      const fieldValue =
        componentsById[graphNode.modelId]?.[
          viewSettings.selectedFieldNameValue
        ];
      return [
        graphNode.modelId,
        typeof fieldValue === 'number' ? fieldValue : null,
      ];
    })
  );

  const allValues = [...fieldValuesByModelId.values()].filter(
    (value): value is number => Number.isFinite(value)
  );

  const selectedValueFieldDomain: [min: number, max: number] = !allValues.length
    ? [10, 10]
    : [Math.min(...allValues), Math.max(...allValues)];

  return {
    ...viewModel,
    hasUserBypassedDataLimit: viewModel.dataLimitCheck.hasUserBypassed,
    numericFields: fieldInterface.getFieldsOfWorkspace(
      viewModel.streamState.context.workspaceId,
      [APIFieldType.NUMBER]
    ),
    selectedValueFieldDomain,
    fieldValuesByModelId,
    zoomComponentId: isContextChange
      ? viewModel.streamState.context.componentId
      : null,
    clearEdges,
    scenarioName,
    viewInstanceId: viewModel.viewInstanceId,
    isViewpointMode: false,
    isViewEmpty: !getGraphNodeCount(viewModel.viewModel),
    performanceTrackingId: viewModel.streamState.performanceTrackingId,
  };
};

const attachIsometricLegendProps = ([
  viewProperties,
  i18nLocale,
  activeDiffMode,
  users,
]: [
  IsometricBlockDiagramViewPropertiesBeforeLegend,
  I18nLocale,
  DiffMode,
  APIOrganizationUser[],
]) => ({
  ...viewProperties,
  i18nLocale,
  errorInfoBoxLabels: getErrorInfoBoxLabels(i18nLocale),
  legendProps: getGraphComponentLegendProps({
    componentMap: viewProperties.streamState.rawGraph.componentMap,
    groupMap: viewProperties.streamState.rawGraph.groupMap,
    edges: viewProperties.streamState.rawGraph.referenceList,
    graphInterface: viewProperties.viewInterface,
    formattingFilters: filterInterface.getFormattingFilters(),
    scopeDiff: viewProperties.scopeDiffState.scopeDiff,
    displayShapesInsteadOfIcons: true,
    activeDiffMode,
    i18nLocale,
    users,
    isNewConditionalFormatting: false,
  }),
});

type GetViewModelArgs = {
  viewSettingsWithChanges$: Observable<
    WorkspaceModeInstanceViewSettings<ViewIds.ISOMETRIC_BLOCK_DIAGRAM>
  >;
  viewInstanceId: string;
};
const getViewModel$ = ({
  viewSettingsWithChanges$,
  viewInstanceId,
}: GetViewModelArgs): Observable<IsometricBlockDiagramViewProperties> => {
  const filteredSourceDataSettings$ = getFilteredViewSettings$(
    viewSettingsWithChanges$,
    IGNORE_VIEW_SETTINGS
  );
  const unfilteredSettings$: Observable<IsometricBlockDiagramViewSettings> =
    viewSettingsWithChanges$.pipe(
      map(({ currentSettings }) => currentSettings)
    );

  const separateReferencesViewSetting$ = viewSettingsWithChanges$.pipe(
    filter(isSeparateReferencesChange)
  );

  /**
   * We need the reducer to run only when certain view settings change, but we need the
   * complete viewSettings to rebuild the model.
   */
  const hasBypassedDataLimit$: Observable<boolean> = action$.pipe(
    withNamespace(viewInstanceId),
    ofType(relationshipDiagramBypassDataLimit),
    map(returnTrue),
    startWith(false)
  );

  const sourceDataReset$ = combineLatest([
    globalViewModelResetSource$,
    filteredSourceDataSettings$,
    hasBypassedDataLimit$,
  ]).pipe(
    withLatestFrom(unfilteredSettings$),
    map(([[globalAppState, , hasUserBypassed], viewSettings]) => ({
      ...globalAppState,
      viewSettings,
      dataLimitCheck: buildViewDataLimitCheck(hasUserBypassed),
    })),
    tap(() => {
      if (viewInstanceId) {
        dispatchAction(notifyViewLoading(viewInstanceId, { isBusy: true }));
      }
    })
  );

  const defaultViewInterface = getDefaultRelationshipDiagramsViewInterface();

  const viewModelSourceData$: Observable<SourceDataShape> = action$.pipe(
    withNamespace(viewInstanceId),
    combineReducers(
      {
        ...emptySourceDataState,
        viewInstanceId,
        viewInterface: defaultViewInterface,
      },
      [
        streamReducer(sourceDataReset$, sourceDataResetReducer),
        streamReducer(componentsChanged$, handleComponentsUpdate),
        streamReducer(
          separateReferencesViewSetting$,
          cloneViewModelForAnimation
        ),
        streamReducer(scopeDiff$, setStateProperty('scopeDiffState')),
        // #region TODO - handle collapsedGroups
        /**
         * While collapsed groups are filtered out in view settings, toggling collased groups triggers an action,
         * and the redcers below rebuild the view model completely + add the changed nodes to the add/update arrays
         * for animations.
         *
         * We could achieve the same by setting view settings, letting the sourceDataResetReducer rebuild on settings
         * change, and adding the intersection of the view settings diff & the view model to the add / update.
         */
        reducer(
          relationshipDiagramToggleCollapseGroup,
          toggleCollapseGroupReducer
        ),
        reducer(relationshipDiagramExpandAllGroups, expandAllGroupsReducer),
        reducer(relationshipDiagramCollapseAllGroups, collapseAllGroupsReducer),
        // #endregion TODO - handle collapsedGroups
      ]
    ),
    tap(({ viewModel }) => {
      dispatchAction(
        readViewComponentIds({
          [VIEW_ID]: getComponentIdsFromViewModel(viewModel),
        })
      );
    })
  );

  const resultWithFilteredViewSettings$ = combineLatest([
    viewModelSourceData$,
    sharedBlockDiagramsRenderingDataSource$,
    components$,
  ]).pipe(map(modelToProps));

  const resultWithLegendProps$ = combineLatest([
    resultWithFilteredViewSettings$,
    i18nLocale$,
    activeDiffMode$,
    allActiveOrgUsers$,
  ]).pipe(map(attachIsometricLegendProps));

  return combineLatest([
    resultWithLegendProps$,
    viewSettingsWithChanges$,
    quickPerspectiveReferenceTypeFilters$,
  ]).pipe(
    map(
      ([
        viewProperties,
        viewSettings,
        quickPerspectiveReferenceTypeFilters,
      ]) => ({
        ...viewProperties,
        viewSettings: viewSettings.currentSettings,
        quickPerspectiveReferenceTypeFilters,
      })
    )
  );
};
export { getViewModel$ };
